import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CreditCard, Check, Receipt } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PortalHeader, EmptyState, fmtDate } from "@/pages/client/_shared";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const STATUS_STYLES = {
  paid: "bg-emerald-500/15 text-emerald-400",
  sent: "bg-amber-500/15 text-amber-400",
  overdue: "bg-red-500/15 text-red-400",
  void: "bg-white/10 text-[#71717A]",
};

export default function ClientBilling() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [params, setParams] = useSearchParams();

  const load = useCallback(() => api.get("/portal/billing").then((r) => setData(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return;
    let tries = 0;
    const poll = async () => {
      try {
        const { data: s } = await api.get(`/portal/billing/status/${sid}`);
        if (s.payment_status === "paid") { toast.success("Payment successful!"); setParams({}); load(); return; }
        if (s.status === "expired" || tries > 6) { setParams({}); return; }
      } catch { setParams({}); return; }
      tries += 1; setTimeout(poll, 2000);
    };
    poll();
  }, [params, setParams, load]);

  const subscribe = async (planId) => {
    setBusy(planId);
    try {
      const { data: r } = await api.post("/portal/billing/checkout", { plan_id: planId, origin_url: window.location.origin });
      window.location.href = r.url;
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setBusy(""); }
  };

  const payInvoice = async (id) => {
    setBusy(id);
    try {
      const { data: r } = await api.post(`/portal/billing/invoices/${id}/pay`, { origin_url: window.location.origin });
      window.location.href = r.url;
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setBusy(""); }
  };

  const sub = data?.subscription;
  return (
    <div className="fv-fade-up">
      <PortalHeader title="Billing & Invoices" subtitle="Manage your subscription and pay invoices securely." />

      <div className="rounded-2xl border border-white/8 bg-[#121214] p-6 mb-8" data-testid="client-subscription-card">
        <div className="flex items-center gap-3 mb-2">
          <CreditCard className="w-5 h-5 text-[#D4AF37]" />
          <span className="text-sm text-[#71717A] uppercase tracking-wider">Current plan</span>
        </div>
        <div className="text-2xl font-heading text-white capitalize">{sub?.tier && sub.tier !== "none" ? sub.tier : "No active plan"}</div>
        <div className="text-sm text-[#A1A1AA] mt-1 capitalize">{sub?.status}</div>
      </div>

      <h3 className="font-heading text-xl text-white mb-3">Plans</h3>
      <div className="grid sm:grid-cols-3 gap-4 mb-8" data-testid="client-plans">
        {(data?.plans || []).map((p) => {
          const active = sub?.tier === p.id && sub?.status === "active";
          return (
            <div key={p.id} className={`rounded-2xl border p-5 ${active ? "border-[#D4AF37]/40 bg-[#151310]" : "border-white/8 bg-[#121214]"}`}>
              <div className="text-white font-medium">{p.name}</div>
              <div className="text-3xl font-heading text-white mt-2">${p.price}<span className="text-sm text-[#71717A]">/mo</span></div>
              <p className="text-xs text-[#71717A] mt-1 mb-4">{p.tagline}</p>
              <ul className="space-y-1.5 mb-5">{p.features.slice(0, 4).map((f) => (
                <li key={f} className="flex items-start gap-2 text-xs text-[#A1A1AA]"><Check className="w-3.5 h-3.5 text-[#D4AF37] shrink-0 mt-0.5" /> {f}</li>))}</ul>
              <Button disabled={active || busy === p.id} onClick={() => subscribe(p.id)} data-testid={`client-plan-${p.id}`}
                className="w-full fv-gold-gradient text-[#0A0A0B] font-semibold rounded-lg hover:opacity-90 disabled:opacity-50">
                {active ? "Current plan" : busy === p.id ? "Redirecting…" : "Subscribe"}</Button>
            </div>
          );
        })}
      </div>

      <h3 className="font-heading text-xl text-white mb-3">Invoice history</h3>
      {(data?.invoices || []).length === 0 ? (
        <EmptyState icon={Receipt} text="No invoices yet." testid="client-invoices-empty" />
      ) : (
        <div className="space-y-2.5" data-testid="client-invoices-list">
          {data.invoices.map((inv) => (
            <div key={inv.invoice_id} className="flex items-center justify-between rounded-xl border border-white/8 bg-[#121214] p-4">
              <div className="min-w-0">
                <div className="text-white text-sm font-medium">{inv.number} — {inv.description}</div>
                <div className="text-xs text-[#71717A]">{fmtDate(inv.created_at)} · ${Number(inv.amount).toFixed(2)}</div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className={`text-xs px-2.5 py-1 rounded-full capitalize ${STATUS_STYLES[inv.status] || STATUS_STYLES.void}`}>{inv.status}</span>
                {["sent", "overdue"].includes(inv.status) && (
                  <Button size="sm" disabled={busy === inv.invoice_id} onClick={() => payInvoice(inv.invoice_id)} data-testid={`client-pay-${inv.invoice_id}`}
                    className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full">{busy === inv.invoice_id ? "…" : "Pay"}</Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
